import React, { useRef, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SERVICES } from "../servicesData";
import SEO from "../components/SEO";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Pill list item ── */
function Pill({ text, i }) {
  const [ref, visible] = useReveal(0.1);
  return (
    <span
      ref={ref}
      className={"sd-pill" + (visible ? " sd-pill--visible" : "")}
      style={{ transitionDelay: `${i * 0.04}s` }}
    >
      {text}
    </span>
  );
}

/* ── What-we-do card ── */
function WhatCard({ item, index }) {
  const [ref, visible] = useReveal(0.1);
  return (
    <div
      ref={ref}
      className={"sd-what-card" + (visible ? " sd-what-card--visible" : "")}
      style={{ transitionDelay: `${(index % 2) * 0.1 + 0.05}s` }}
    >
      <span className="sd-what-num">0{index + 1}</span>
      <h4 className="sd-what-title">{item.title}</h4>
      <p className="sd-what-body">{item.desc}</p>
    </div>
  );
}

export default function ServiceDetailPage() {
  const { id } = useParams();
  const service = SERVICES.find((s) => s.id === id);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!service) {
    return (
      <div className="sd-notfound">
        <p>Service not found.</p>
        <Link to="/services">← Back to services</Link>
      </div>
    );
  }

  const currentIndex = SERVICES.findIndex((s) => s.id === id);
  const prevService = SERVICES[currentIndex - 1];
  const nextService = SERVICES[currentIndex + 1];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://ovarddigital.com/services/${service.id}`,
    "name": service.title,
    "description": `${service.tagline} ${service.detail.body}`,
    "provider": { "@id": "https://ovarddigital.com/#organization" },
    "areaServed": { "@type": "Country", "name": "India" },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${service.title} Services`,
      "itemListElement": service.items.map((item, i) => ({
        "@type": "Offer",
        "position": i + 1,
        "itemOffered": {
          "@type": "Service",
          "name": item,
          "provider": { "@id": "https://ovarddigital.com/#organization" }
        }
      }))
    }
  };

  return (
    <div className="sd-page">

      <SEO
        title={`${service.title} Services`}
        description={`${service.tagline} OVARD Digital provides ${service.title.toLowerCase()} services in India — ${service.items.slice(0, 5).join(", ")} and more.`}
        canonical={`/services/${service.id}`}
        ogImage={service.hero}
        schema={serviceSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: service.title, path: `/services/${service.id}` }
        ]}
      />

      <Nav />

      {/* ── Hero ── */}
      <HeroSection service={service} />

      {/* ── Detail body ── */}
      <div className="sd-body">
        <div className="container sd-body-inner">

          {/* Left sticky sidebar */}
          <aside className="sd-sidebar">
            <SidebarContent service={service} currentIndex={currentIndex} />
          </aside>

          {/* Right content column */}
          <main className="sd-main">
            <IntroSection service={service} />
            <WhatSection service={service} />
            <AllServicesSection service={service} />
          </main>
        </div>
      </div>

      {/* ── Pagination ── */}
      <ServicePagination prev={prevService} next={nextService} />

      {/* ── CTA ── */}
      <ServiceCTA service={service} />
      <Footer />

    </div>
  );
}

/* ── Hero ── */
function HeroSection({ service }) {
  const [play, setPlay] = useState(false);
  useEffect(() => { const t = setTimeout(() => setPlay(true), 60); return () => clearTimeout(t); }, []);

  return (
    <section className={"sd-hero" + (play ? " sd-hero--play" : "")}>
      {/* Full-bleed image */}
      <div
        className="sd-hero-img"
        style={{ backgroundImage: `url(${service.hero})` }}
      >
        <div className="sd-hero-overlay" />
      </div>

      <div className="sd-hero-grain" aria-hidden="true" />

      <div className="container sd-hero-content">
        <div className="sd-hero-eyebrow">
          <span className="sd-hero-num">{service.num}</span>
          <span className="sd-hero-sep">·</span>
          OVARD DIGITAL
        </div>
        <h1 className="sd-hero-h1">
          <span className="sd-hero-line"><span>{service.title}</span></span>
        </h1>
        <p className="sd-hero-tagline">{service.tagline}</p>
      </div>

      {/* Bottom scroll cue */}
      <div className="sd-hero-scroll">
        <span className="sd-hero-scroll-text">Scroll</span>
        <span className="sd-hero-scroll-line" />
      </div>
    </section>
  );
}

/* ── Sidebar ── */
function SidebarContent({ service, currentIndex }) {
  return (
    <>
      <div className="sd-sidebar-label">Service</div>
      <div className="sd-sidebar-num">{service.num} / 13</div>

      <div className="sd-sidebar-divider" />

      <p className="sd-sidebar-tagline">{service.tagline}</p>

      <div className="sd-sidebar-divider" />

      {/* All services nav */}
      <nav className="sd-sidebar-nav" aria-label="All services">
        {SERVICES.map((s, i) => (
          <Link
            key={s.id}
            to={`/services/${s.id}`}
            className={"sd-sidebar-link" + (s.id === service.id ? " sd-sidebar-link--active" : "")}
          >
            <span className="sd-sidebar-link-num">{s.num}</span>
            <span>{s.title}</span>
          </Link>
        ))}
      </nav>

      <div className="sd-sidebar-cta">
        <Link to="/#contact" className="sd-sidebar-btn">
          Start a project
          <svg width="12" height="9" viewBox="0 0 14 10" fill="none">
            <path d="M8 1L13 5L8 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13 5H1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </Link>
      </div>
    </>
  );
}

/* ── Intro ── */
function IntroSection({ service }) {
  const [ref, visible] = useReveal(0.15);
  return (
    <div className={"sd-intro" + (visible ? " sd-intro--visible" : "")} ref={ref}>
      <h2 className="sd-intro-headline">{service.detail.headline}</h2>
      <p className="sd-intro-body">{service.detail.body}</p>
    </div>
  );
}

/* ── What we do ── */
function WhatSection({ service }) {
  const [ref, visible] = useReveal(0.1);
  return (
    <div ref={ref}>
      <div className={"sd-what-head" + (visible ? " sd-what-head--visible" : "")}>
        <span className="sd-section-eyebrow">What's included</span>
        <h3 className="sd-what-h3">Every service we offer in this category</h3>
      </div>
      <div className="sd-what-grid">
        {service.detail.what.map((item, i) => (
          <WhatCard key={item.title} item={item} index={i} />
        ))}
      </div>
      {/* Feature image */}
      <div className="sd-what-img-wrap">
        <img
          src={service.detail.image}
          alt={`${service.title} services by OVARD Digital — digital marketing agency India`}
          className="sd-what-img"
          loading="lazy"
        />
        <div className="sd-what-img-badge">
          <span className="sd-what-img-badge-num">{service.num}</span>
          <span className="sd-what-img-badge-label">{service.title}</span>
        </div>
      </div>
    </div>
  );
}

/* ── All services (pills) ── */
function AllServicesSection({ service }) {
  const [ref, visible] = useReveal(0.1);
  return (
    <div ref={ref} className={"sd-all" + (visible ? " sd-all--visible" : "")}>
      <span className="sd-section-eyebrow">All services in this category</span>
      <div className="sd-pills">
        {service.items.map((item, i) => (
          <Pill key={item} text={item} i={i} />
        ))}
      </div>
    </div>
  );
}

/* ── Pagination ── */
function ServicePagination({ prev, next }) {
  return (
    <nav className="sd-pagination">
      <div className="container sd-pagination-inner">
        {prev ? (
          <Link to={`/services/${prev.id}`} className="sd-pag-link sd-pag-link--prev">
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
              <path d="M6 1L1 5L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1 5H13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            <div>
              <span className="sd-pag-dir">Previous</span>
              <span className="sd-pag-name">{prev.title}</span>
            </div>
          </Link>
        ) : <div />}

        <Link to="/services" className="sd-pag-all">All services</Link>

        {next ? (
          <Link to={`/services/${next.id}`} className="sd-pag-link sd-pag-link--next">
            <div>
              <span className="sd-pag-dir">Next</span>
              <span className="sd-pag-name">{next.title}</span>
            </div>
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
              <path d="M8 1L13 5L8 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </Link>
        ) : <div />}
      </div>
    </nav>
  );
}

/* ── CTA ── */
function ServiceCTA({ service }) {
  const [ref, visible] = useReveal(0.3);
  return (
    <section
      className={"sd-cta" + (visible ? " sd-cta--visible" : "")}
      ref={ref}
    >
      <div className="sd-cta-grain" aria-hidden="true" />
      <div className="container sd-cta-inner">
        <div className="sd-cta-left">
          <span className="sd-cta-eyebrow">{service.num} · {service.title}</span>
          <h2 className="sd-cta-h2">
            Ready to build something?
          </h2>
          <p className="sd-cta-sub">
            Tell us what you need. We'll tell you exactly how we'd approach it.
          </p>
        </div>
        <div className="sd-cta-right">
          <Link to="/#contact" className="sd-cta-btn sd-cta-btn--primary">
            <span>Start a conversation</span>
            <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
              <path d="M9 1L14 5.5L9 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 5.5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </Link>
          <Link to="/services" className="sd-cta-btn sd-cta-btn--ghost">
            Explore all services
          </Link>
        </div>
      </div>
    </section>
  );
}
